//libraries
import React, { Suspense } from 'react';
import styled from 'styled-components';

// styled components
import { RelatedArticlesSection, RelatedArticlesHeading } from 'pages/Articles/components/Article';

// styles
import { Frame, CentredFlex, Flex, ColumnedFlex, SubmitButton } from 'shared/styledComponents';
import { H1, H2, Title, Text } from 'shared/typography';
import devices from 'theme/devices';

// assets
import mainSectionImageSrc from 'assets/images/main-section.svg';
import connectionExamplesImageSrc from 'assets/images/connect-examples-section.svg';
import servicesSectionImageSrc from 'assets/images/services-section.svg';

// custom components
import Layout from 'components/Layout';
import Loader from 'components/Loader';
import RealStoriesPreview from 'pages/RealStory/components/RealStoriesPreview';
import ConnectionWithProviderExamples from './ConnectionWithProviderExamples';
import forInclusionImageSrc from 'assets/images/woman-texting.png';
import fertilityOutreachLogo from 'assets/images/fertility-outreach-logo.svg';

const BaseFindProviderForm = React.lazy(() => import('modules/FindProviders/BaseFindProviderForm'));
const RelatedArticles = React.lazy(() => import('pages/Articles/components/RelatedArticles'));

const MainSectionWrapper = styled(Flex)`
  background-color: ${({ theme: { colors } }): string => colors.secondary};
  overflow: hidden;
  padding-top: 8vh;

  @media ${devices.mobile} {
    padding-top: 25px;
  }
`;

const MainSection = styled(Frame)`
  flex-direction: column;
  position: relative;
  padding-bottom: 10vh;

  @media ${devices.mobile} {
    padding-bottom: 85px;
  }
`;

const MainSectionTitle = styled(H1)`
  max-width: 700px;

  @media ${devices.mobile} {
    max-width: 250px;
  }
`;

const MainSectionSubtitle = styled(Title)`
  margin-top: 25px;
  max-width: 510px;
  font-size: 24px;

  @media ${devices.mobile} {
    max-width: 250px;
  }
`;

const MainSectionImage = styled.img`
  position: absolute;
  width: 655px;
  top: -60px;
  right: -200px;

  @media ${devices.mobile} {
    width: 232px;
    height: 260px;
    top: 15px;
    right: -120px;
    transform: rotate(170deg);
  }
`;

const ConnectSection = styled(Flex)`
  margin-bottom: 175px;

  @media ${devices.mobile} {
    flex-direction: column;
    align-items: center;
  }
`;

const ConnectSectionInfo = styled(ColumnedFlex)`
  position: relative;
  margin-right: 95px;

  @media ${devices.mobile} {
    margin: auto;
    margin-bottom: 45px;
  }
`;

const ConnectSectionTitle = styled(H2)`
  max-width: 485px;
  margin-top: 60px;

  @media ${devices.mobile} {
    margin-top: 0px;
  }
`;

const ConnectSectionSubtitle = styled(Text)`
  max-width: 380px;
  margin-top: 27px;

  @media ${devices.mobile} {
    max-width: 250px;
    margin-top: 22px;
    font-size: 14px;
  }
`;

const ConnectSectionImage = styled.img`
  position: absolute;
  width: 535px;
  left: -230px;
  top: 445px;
  z-index: 1;

  @media ${devices.mobile} {
    display: none;
  }
`;

const ServicesSectionWrapper = styled(Frame)`
  justify-content: center;
  margin-bottom: 100px;

  @media ${devices.mobile} {
    margin-bottom: 250px;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-rows: repeat(4, 340px);
  grid-template-columns: repeat(4, 270px);
  grid-gap: 19px;
  margin-top: 145px;
  align-items: center;
  text-align: center;
  & #grid-title {
    grid-column-start: 3;
    grid-column-end: 5;
    grid-row-start: 1;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    text-align: start;
    padding-left: 100px;
    margin-bottom: 10px;
  }
  & #grid-icon {
    grid-column-start: 1;
    grid-column-end: 2;
    grid-row-start: 5;
    margin-top: 250px;
    margin-left: -30px;
    position: relative;
    z-index: -1;
  }
  @media ${devices.mobile} {
    width: 100%;
    align-items: center;
    padding: 0;
    padding-top: 100px;
    margin-top: 85px;
    grid-template-rows: repeat(7, 190px);
    grid-template-columns: repeat(2, minmax(150px, 50%));
    grid-auto-flow: row;
    grid-gap: 11px;

    & #grid-title {
      grid-column-start: 1;
      grid-column-end: 3;
      grid-row-start: 1;
      margin-bottom: 105px;
      padding-left: 15px;
    }
    & #grid-icon {
      margin-top: -70px;
      margin-left: -50px;

      grid-row-start: 9;
      grid-column-start: 1;
      grid-column-end: 2;
    }
  }
`;

const ServicesSectionTitle = styled(H2)`
  width: 420px;
  height: 145px;

  @media ${devices.mobile} {
    max-width: 290px;
    height: 105px;
  }
`;

const ServicesSectionSubtitle = styled(Text)`
  max-width: 345px;
  width: 100%;
  height: 70px;
`;

const ServicesSectionImage = styled.img`
  position: absolute;
  height: 390px;
  width: 350px;
  left: -225px;
  top: -320px;

  @media ${devices.mobile} {
    width: 250px;
    height: 284px;
    left: -25px;
    top: -40px;
  }
`;

const Card = styled(CentredFlex)<any>(
  ({ theme: { colors } }) => `
    width: 100%;
    height: 100%;
    flex-direction: column;
    justify-content: space-between;
    background-color: ${colors.secondary};
    cursor: pointer;

    &:hover {
      & .card-icon {
        height: 0;
        margin-top: 10px;
        opacity: 0;
      }
      & .card-description{
        height: auto;
        opacity: 1;
        transition: opacity .35s ease-in-out;
      }
    }

    @media ${devices.mobile} {
      &:hover {
        & .card-icon {
          margin: 0px;
          margin-top:10px;
        }
        & .card-description{
          overflow: hidden;
          text-overflow: ellipsis;
          display: -webkit-box;
          -webkit-line-clamp: 6;
          -webkit-box-orient: vertical;
        }
      }
    }
  `,
);

const ServicesSectionIcon = styled.img<any>`
  height: auto;
  margin-top: 80px;
  opacity: 1;
  transition: opacity 0.35s ease-in-out;

  @media ${devices.mobile} {
    width: 76px;
    max-height: 65px;
    margin: 40px 0;
  }
`;

const ServiceCardDescription = styled(Text)`
  height: 0;
  width: 70%;
  opacity: 0;
`;

const ServiceCardTitle = styled(Title)`
  margin-bottom: 25px;
`;

const ConnectionWithProviderExamplesWrapper = styled(Flex)`
  width: 100%;
  max-width: 460px;
  min-height: 605px;
  justify-content: center;
  align-items: center;
  box-shadow: 0 2px 14px 0 rgba(0, 0, 0, 0.06);

  @media ${devices.mobile} {
    max-width: 390px;
    width: 100%;
  }
`;

const ForInclusionWrapper = styled(ColumnedFlex)`
  position: relative;
  background-color: ${({ theme: { colors } }): string => colors.forBeige};
  justify-content: center;
  overflow: hidden;
  padding-top: 5rem;
  padding-bottom: 5rem;
  margin-bottom: 4rem;
  @media ${devices.mobile} {
    position: relative;
    flex-direction: column;
    padding-top: 4rem;
    padding-bottom: 4rem;
    margin-bottom: 7.5rem;
  }
`;

const ForInclusionInsideWrapper = styled(Flex)`
  position: relative;
  width: 100%;
  max-width: 1140px;
  margin: 0 auto;
  justify-content: center;
  gap: 2rem;
  z-index: 3;
  @media ${devices.mobile} {
    position: static;
    max-width: 350px;
    flex-direction: column;
    width: 100%;
  }
`;

const ForInclusionWhiteBackground = styled.div`
  background-color: ${({ theme: { colors } }): string => colors.white};
  width: 750px;
  height: 308px;
  position: absolute;
  top: -9%;
  right: 0;
  margin-right: 7rem;
  z-index: -1;
  @media ${devices.mobile} {
    width: 100vw;
    height: 450px;
    margin-right: 0;
    top: 25%;
    right: 0;
  }
`;

const ForInclusionImageWrapper = styled(Flex)`
  justify-content: flex-end;
  width: 40%;
  @media ${devices.mobile} {
    width: 100%;
    justify-content: center;
  }
`;

const ForInclusionHeroImage = styled.img`
  width: 300px;
  height: 263.25px;
  object-fit: fill;
  @media ${devices.mobile} {
    width: 90%;
    height: 90%;
`;

const ForInclusionContentWrapper = styled(ColumnedFlex)`
  width: 46%;
  height: 100%;
  justify-content: space-between;
  padding: 0 8rem 0 2rem;
  margin: 0;
  text-align: center;
  gap: 1rem;
  @media ${devices.mobile} {
    width: 100%;
    padding: 0 1rem;
    gap: 1rem;
  }
`;

const ForLogo = styled.img`
  margin: 0 auto;
  width: 90%;
`;

const ForInclusionText = styled.p`
  font-family: ${({ theme: { fonts } }): string => fonts.brandonGrotesqueRegular};
  font-size: 1.15rem;
  @media ${devices.mobile} {
    font-size: 1rem;
  }
`;

const ForInclusionMediumText = styled.span`
  font-family: ${({ theme: { fonts } }): string => fonts.brandonGrotesqueMedium};
  font-weight: bold;
  font-size: 1.15rem;
  @media ${devices.mobile} {
    font-size: 1rem;
  }
`;

const ForInclusionCTAButtonWrapper = styled.div`
  position: relative;
  width: 100%;
  font-weight: bold;
  align-items: center;
  justify-content: center;
`;

const ForInclusionCTAButton = styled(SubmitButton)`
  font-family: ${({ theme: { fonts } }): string => fonts.brandonGrotesqueMedium};
  font-weight: bold;
  color: ${({ theme: { colors } }): string => colors.white};
  background-color: ${({ theme: { colors } }): string => colors.black};
  z-index: 2;
  position: relative;
  font-size: 1.15rem;
  line-height: 1.5rem;
  padding: 1rem 2rem;
  &:hover {
    background-color: ${({ theme: { colors } }): string => colors.forPlumDark};
  }
  @media ${devices.mobile} {
    width: 60%;
    font-size: 1rem;
    padding: 0.5rem 1.5rem;
`;

const ForInclusionCTAButtonBackground = styled.div`
  position: absolute;
  width: 80%;
  height: 2.8rem;
  bottom: -2rem;
  width: 100%;
  left: 0;
  -webkit-transform: skewX(-10deg);
  z-index: 1;
  background-color: ${({ theme: { colors } }): string => colors.forYellow};
  @media ${devices.mobile} {
    width: 100%;
    left: 0;
`;

const VeevaCodeWrapper = styled(Flex)`
  position: absolute;
  justify-content: center;
  width: 100%;
  bottom: 0.75rem;
  font-family: ${({ theme: { fonts } }): string => fonts.brandonGrotesqueRegular};
  font-size: 0.75rem;
  color: ${({ theme: { colors } }): string => colors.black};
  text-align: center;
   @media ${devices.mobile} {
    bottom: 0.5rem;
`;

interface HomeProps {
  articles: any;
  stories: any;
  providerConnections: any;
  onRelatedArticleClick: (url: string) => () => void;
  onStoryClick: (url: string) => () => void;
  homePageContent: any;
  cardServices: any[];
  handleRedirectToServiceLanding: (service: any) => void;
  onProviderConnectionClick: (providerSlug: string) => () => void;
  isProviderConnectionsLoading: boolean;
  isStoriesLoading: boolean;
  instagramMedia: any;
}

const Home = ({
  articles,
  stories,
  providerConnections,
  onRelatedArticleClick,
  handleRedirectToServiceLanding,
  onProviderConnectionClick,
  cardServices,
  onStoryClick,
  instagramMedia,
  isProviderConnectionsLoading,
  isStoriesLoading,
  homePageContent: {
    mainSectionTitle,
    mainSectionSubtitle,
    connectSectionTitle,
    connectSectionSubtitle,
    servicesSectionTitle,
    servicesSectionSubtitle,
  },
}: HomeProps): JSX.Element => (
  <Layout instagramMedia={instagramMedia}>
    <MainSectionWrapper>
      <MainSection>
        <Flex>
          <ColumnedFlex>
            <MainSectionTitle>{mainSectionTitle}</MainSectionTitle>
            <MainSectionSubtitle>{mainSectionSubtitle}</MainSectionSubtitle>
          </ColumnedFlex>
          <MainSectionImage data-src={mainSectionImageSrc} alt="main section" className="lazyload" />
        </Flex>
      </MainSection>
    </MainSectionWrapper>
    <Frame>
      <Suspense fallback={<div>Loading...</div>}>
        <BaseFindProviderForm />
      </Suspense>

      <ServicesSectionWrapper>
        <Grid>
          {cardServices.map((card: any) => (
            <Card key={card.id} onClick={handleRedirectToServiceLanding(card)}>
              <ServicesSectionIcon className="card-icon lazyload" data-src={card.src} alt="Service Icon" />
              <ServiceCardDescription className="card-description" size="small">
                {card.description}
              </ServiceCardDescription>
              <ServiceCardTitle>{card.value}</ServiceCardTitle>
            </Card>
          ))}
          <div id="grid-title">
            <ServicesSectionTitle>{servicesSectionTitle}</ServicesSectionTitle>
            <ServicesSectionSubtitle>{servicesSectionSubtitle}</ServicesSectionSubtitle>
          </div>
          <div id="grid-icon">
            <ServicesSectionImage data-src={servicesSectionImageSrc} alt="services section" className="lazyload" />
          </div>
        </Grid>
      </ServicesSectionWrapper>
      <ConnectSection>
        <ConnectSectionInfo>
          <ConnectSectionTitle>{connectSectionTitle}</ConnectSectionTitle>
          <ConnectSectionSubtitle>{connectSectionSubtitle}</ConnectSectionSubtitle>
          <ConnectSectionImage
            data-src={connectionExamplesImageSrc}
            alt="connect with provider examples"
            className="lazyload"
          />
        </ConnectSectionInfo>
        <ConnectionWithProviderExamplesWrapper>
          {isProviderConnectionsLoading ? (
            <Loader />
          ) : (
            <ConnectionWithProviderExamples
              providerConnections={providerConnections}
              onProviderConnectionClick={onProviderConnectionClick}
            />
          )}
        </ConnectionWithProviderExamplesWrapper>
      </ConnectSection>
      {isStoriesLoading ? <Loader /> : <RealStoriesPreview stories={stories} onStoryClick={onStoryClick} />}
    </Frame>
    <ForInclusionWrapper>
      <ForInclusionInsideWrapper>
        <ForInclusionWhiteBackground />
        <ForInclusionImageWrapper>
          <Suspense fallback={<div>Loading...</div>}>
            <ForInclusionHeroImage
              data-src={forInclusionImageSrc}
              alt="woman in a knit sweater at her office texting on her cell phone"
              className="lazyload"
            />
          </Suspense>
        </ForInclusionImageWrapper>
        <ForInclusionContentWrapper>
          <Suspense fallback={<div>Loading...</div>}>
            <ForLogo data-src={fertilityOutreachLogo} alt="fertility outreach logo" className="lazyload" />
          </Suspense>
          <ForInclusionText>
            Our newest support resource, <ForInclusionMediumText>Fertility Outreach</ForInclusionMediumText>, provides
            you with personalized guidance and advice from trained fertility coaches at your fingertips.
          </ForInclusionText>
          <ForInclusionCTAButtonWrapper>
            <a href={'https://outreach.fertilityoutloud.com/'} rel={'noopener noreferrer'} target={'_blank'}>
              <ForInclusionCTAButton>Sign Up to text a coach</ForInclusionCTAButton>
            </a>
            <ForInclusionCTAButtonBackground />
          </ForInclusionCTAButtonWrapper>
        </ForInclusionContentWrapper>
      </ForInclusionInsideWrapper>
      <VeevaCodeWrapper>
        <p>US-RMMH-2300266</p>
      </VeevaCodeWrapper>
    </ForInclusionWrapper>
    <Frame>
      {articles && !!articles.length && (
        <RelatedArticlesSection as="section">
          <RelatedArticlesHeading>Related Articles</RelatedArticlesHeading>
          <Suspense fallback={<div>Loading...</div>}>
            <RelatedArticles articles={articles} onRelatedArticleClick={onRelatedArticleClick} />
          </Suspense>
        </RelatedArticlesSection>
      )}
    </Frame>
  </Layout>
);

export default Home;
